import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, DataGrid, LoadPanel, Popup, Toast } from "devextreme-react";
import { PopupRef, Position, ToolbarItem } from "devextreme-react/popup";
import { DataGridRef, Column, Selection, DataGridTypes } from "devextreme-react/data-grid";
import { useScreenSize } from "../../utils/media-query";
import useApiConfiguration from "../../apiConfig";
import { AllegatiTemporaneiApi, GiustificativiApi } from "../../data/apis";
import { AttachmentResponseDTO, GiustificativiUploadAllegatiDTO } from "../../data";
import { useAttachmentDataSource } from "../../datasources/attachmentDataSource";
import { PopupPdfViewer } from '..';
import { PopupPdfViewerHandle } from '../pdfViewer/popupPdfViewer';

export type PopupCaricaArchivioHandle = {
    show: (rimborsi: GiustificativiUploadAllegatiDTO[]) => void;
};

type Props = {
    onHide: () => void;
};

type State = {
    rimborsi: GiustificativiUploadAllegatiDTO[],
    visible: boolean
};

const PopupCaricaArchivio: React.ForwardRefRenderFunction<PopupCaricaArchivioHandle, Props> = (props, ref) => {
    const popupRef = useRef<PopupRef>(null);
    const gridRef = useRef<DataGridRef>(null);
    const pdfViewerRef = useRef<PopupPdfViewerHandle>(null);
    const apiConfig = useApiConfiguration();
    const [toastConfig, setToastConfig] = useState({ isVisible: false, isOk: true, message: '' } as { isVisible: boolean, isOk: boolean, message: string });
    const { isXSmall, isSmall } = useScreenSize();

    const [loading, setLoading] = useState<boolean>(false);
    const [state, setState] = useState<State>({ rimborsi: [], visible: false });

    function hideInfo() {
        props.onHide();
        setState({ rimborsi: [], visible: false });
    };

    useImperativeHandle(ref, () => ({
        show(rimborsi) {
            setState({ rimborsi, visible: true });
        }
    }));

    const fileAllegatoRender = (cell: DataGridTypes.ColumnCellTemplateData) => {
        const idAllegato = cell.data.idAllegato;
        
        return (
            <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left', gap: '10px', width: '100%' }}>
                <Button icon="pdffile" text="" stylingMode="text" onClick={() => showAllegato(idAllegato)} />
            </div>
        );
    };

    const showAllegato = async (idAllegato: number) => {
        setLoading(true);

        var api = new AllegatiTemporaneiApi(apiConfig);
        const response = await api.getAttachmentById({ idFileAllegato: idAllegato });
        if (response?.base64) {
            const fileContent = atob(response.base64);
            const byteArray = new Uint8Array(fileContent.length);
            for (let i = 0; i < fileContent.length; i++)
                byteArray[i] = fileContent.charCodeAt(i);

            const mimeType = "application.pdf";
            const fileBlob = new Blob([byteArray], { type: mimeType });

            if (fileBlob)
                pdfViewerRef.current?.show(fileBlob);
        }

        setLoading(false);
    };

    const upload = async () => {
        setLoading(true);
        
        const dati = gridRef.current?.instance().getSelectedRowsData() as AttachmentResponseDTO[];
        if (!dati.length) {
            setToastConfig({
                isVisible: true,
                isOk: false,
                message: "Selezionare almeno un file da caricare"
            });
            return;
        }

        var api = new GiustificativiApi(apiConfig);
        api.uploadAllegatiArchivio({
            giustificativiUploadArchivioDTO: {
                rimborsi: state.rimborsi,
                idsArchivio: dati.map(d => d.idAllegato)
            }
        }).then((res) => {
            setToastConfig({
                isVisible: true,
                isOk: true,
                message: "File caricato/i con successo"
            });

            hideInfo();
        }).catch((error) => {
            console.error(error);

            setToastConfig({
                isVisible: true,
                isOk: false,
                message: "Errore durante il caricamento"
            });
        }).finally(() => setLoading(false));
    };

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    return (
        <React.Fragment>
            <Popup
                visible={state.visible} fullScreen={(isSmall || isXSmall)}
                dragEnabled={false} hideOnOutsideClick={false}
                showCloseButton showTitle ref={popupRef} title="Carica da Archivio"
                container=".dx-viewport" width={800} height={500}
                onHidden={hideInfo}
            >
                { loading && <LoadPanel style={{ zIndex: 9999 }} position={'center'} visible shading shadingColor="rgba(0, 0, 0, 0.4)" /> }

                <Position at="center" my="center" of="" collision="fit" />

                <div style={{ height: "100%" }} id="divGridAllegatiArchivio">
                    <DataGrid 
                        id="gridAllegatiArchivio" ref={gridRef} key="idAllegato" columnAutoWidth
                        dataSource={useAttachmentDataSource()} showBorders className="grid theme-dependent"
                        rowAlternationEnabled noDataText="Nessun allegato in archivio"
                    >
                        <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" />

                        <Column dataField="idAllegato" visible={false} />
                        <Column dataField="fileName" caption="Nome File" />
                        <Column dataField="dataCreazione" caption="Data" />
                        <Column cellRender={fileAllegatoRender} />
                    </DataGrid>
                </div>

                <ToolbarItem 
                    widget="dxButton" 
                    toolbar="bottom" 
                    location="center" 
                    options={{
                        text: "Carica",
                        type: "default",
                        stylingMode: "contained",
                        onClick: upload
                    }}
                />

                <ToolbarItem 
                    widget="dxButton"
                    toolbar="bottom"
                    location="center"
                    options={{
                        text: "Chiudi",
                        stylingMode: "outlined",
                        onClick: hideInfo
                    }}
                />

                <PopupPdfViewer ref={pdfViewerRef} />
            </Popup>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type={toastConfig.isOk ? "success" : "error"}
                onHiding={onHidingToast} closeOnClick
                displayTime={3000}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />
        </React.Fragment>
    );
};

export default React.forwardRef(PopupCaricaArchivio);